import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Archive,
  Bookmark,
  CheckCircle2,
  ClipboardList,
  FileText,
  Layers,
  Send,
  ShieldAlert,
  Sparkles,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type BulkKeywordAction,
  type ClusterView,
  type PipelineTask,
  type Priority,
  summariseAiAssign,
} from "../../lib/api.js";
import { ClusterPicker } from "../../components/ui/ClusterPicker.js";
import { routeQuery } from "../../lib/useRoute.js";
import { ResolvePanel } from "./ResolvePanel.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";

/**
 * Tasks inbox — categorised pipeline TODOs derived server-side from the
 * current DB state. Each row supports:
 *   - Open  → jumps to the Pipeline detail panel
 *   - Priority dropdown (inline edit, saves immediately)
 *   - Cluster dropdown (inline edit)
 *   - Archive (soft delete; row disappears from this inbox)
 *
 * Bulk-select mode (checkboxes) adds an action bar with: Archive selected /
 * Set priority / Set cluster — for cleaning up large batches of test data
 * without clicking row-by-row.
 *
 * Important: tasks are DERIVED from the keyword state, not stored. Archiving
 * a keyword (status='archived') removes it from the tasks query result on the
 * server. There's no "task table" to mutate.
 */

const CATEGORY_META: Record<
  PipelineTask["category"],
  { icon: LucideIcon; label: string; tint: string; chip: string }
> = {
  needs_enrich:        { icon: Layers,        label: "Needs enrichment",       tint: "bg-warn-soft text-amber-700",     chip: "pill-warn" },
  needs_scrape:        { icon: AlertCircle,   label: "Needs competitor scrape", tint: "bg-warn-soft text-amber-700",     chip: "pill-warn" },
  needs_brief:         { icon: Bookmark,      label: "Ready for brief",        tint: "bg-info-soft text-blue-700",      chip: "pill-info" },
  ready_to_produce:    { icon: FileText,      label: "Ready to produce",       tint: "bg-brand-50 text-brand-700",      chip: "pill-brand" },
  ready_to_distribute: { icon: Send,          label: "Ready to distribute",    tint: "bg-accent-soft text-emerald-700", chip: "pill-ok" },
  needs_refresh:       { icon: CheckCircle2,  label: "Refresh stale articles", tint: "bg-info-soft text-blue-700",      chip: "pill-info" },
  // Every other category is "this keyword is missing a step". This one is "the
  // autopilot will write this unsupervised and something is wrong with it", so
  // it reads as an alert rather than as outstanding work.
  preflight:           { icon: ShieldAlert,   label: "Autopilot pre-flight",   tint: "bg-danger-soft text-red-700",     chip: "pill-danger" },
};

// Pre-flight leads. Everything below it is work that waits for you; pre-flight
// is work that happens WITHOUT you at 06:00 unless you look.
const CATEGORY_ORDER: PipelineTask["category"][] = [
  "preflight",
  "ready_to_distribute",
  "ready_to_produce",
  "needs_brief",
  "needs_scrape",
  "needs_enrich",
  "needs_refresh",
];

const PRIORITIES: Priority[] = ["urgent", "high", "medium", "low"];

/**
 * What closes a finding, said in the row itself. The inbox was 74 findings with
 * two buttons and no way to tell which were waiting on you — this is the label
 * that answers "so what do I do with this one".
 */
const RESOLUTION_LABEL: Record<string, string> = {
  auto: "handled automatically",
  apply: "one click",
  enrich: "re-fetch",
  decide: "your call",
  info: "context",
};

const RESOLUTION_STYLE: Record<string, string> = {
  auto: "bg-accent-soft text-emerald-700",
  apply: "bg-brand-50 text-brand-700",
  enrich: "bg-info-soft text-blue-700",
  decide: "bg-warn-soft text-amber-800",
  info: "bg-line-soft text-muted",
};

export function TasksPage() {
  const qc = useQueryClient();
  const [activeCategory, setActiveCategory] = useState<PipelineTask["category"] | "all">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  // Deep-link: `#/tasks?open=<keywordId>` rings that row and scrolls to it.
  // The notification bell links here rather than to Automations — a finding you
  // have to act on belongs in the inbox, and landing at the top of a long list
  // with no idea which row was meant is the same as not linking at all.
  const [highlightId, setHighlightId] = useState<string | null>(() => routeQuery().get("open"));
  useEffect(() => {
    const read = () => setHighlightId(routeQuery().get("open"));
    window.addEventListener("hashchange", read);
    return () => window.removeEventListener("hashchange", read);
  }, []);

  const { data, isPending, error } = useQuery({
    queryKey: ["dashboard", "tasks"],
    queryFn: () => api.getTasks(),
    refetchInterval: 30_000,
  });
  const clustersQuery = useQuery({ queryKey: ["pipeline", "clusters"], queryFn: () => api.listClusters() });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["pipeline"] });
    setSelected(new Set());
  };

  const bulkMutation = useMutation({
    mutationFn: (body: { action: BulkKeywordAction; ids: string[]; priority?: Priority; clusterId?: string | null; reason?: string }) =>
      api.bulkKeywordAction(body),
    onSuccess: (r) => {
      const label =
        r.action === "archive"      ? "archived" :
        r.action === "reject"       ? "rejected" :
        r.action === "delete"       ? "deleted" :
        r.action === "set_priority" ? "re-prioritised" :
        "re-clustered";
      setBanner({
        kind: r.failed === 0 ? "ok" : "error",
        text: `${r.succeeded} ${label}${r.failed > 0 ? ` · ${r.failed} failed` : ""}`,
      });
      invalidateAll();
    },
    onError: (e: Error) => setBanner({ kind: "error", text: e.message }),
  });

  const [aiProgress, setAiProgress] = useState<string | null>(null);
  const aiAssignSelected = async (ids: string[]) => {
    setAiProgress(`0/${ids.length}`);
    try {
      const results = await api.aiAssignClustersAll(ids, (done, total) => setAiProgress(`${done}/${total}`));
      setBanner({ kind: results.some((r) => r.outcome === "error") ? "error" : "ok", text: summariseAiAssign(results) });
      invalidateAll();
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setAiProgress(null);
    }
  };

  if (error) {
    return (
      <div className="card p-8 text-sm text-danger">
        Failed to load tasks: {(error as Error).message}
      </div>
    );
  }

  const allTasks = data?.tasks ?? [];
  const visible = activeCategory === "all" ? allTasks : allTasks.filter((t) => t.category === activeCategory);

  // Group for rendering when "all" is selected.
  const groups = CATEGORY_ORDER
    .map((c) => ({ category: c, tasks: (data?.byCategory[c] ?? []) }))
    .filter((g) => g.tasks.length > 0);

  // Selection is in terms of unique keyword ids (not task ids — multiple
  // categories may surface the same keyword once each, and the operator
  // generally wants to act on the KEYWORD, not just one row of it).
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const allVisibleIds = useMemo(() => Array.from(new Set(visible.map((t) => t.keywordId))), [visible]);
  const allSelected = selectedIds.length > 0 && allVisibleIds.every((id) => selected.has(id));

  const toggleSelect = (keywordId: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(keywordId)) next.delete(keywordId);
      else next.add(keywordId);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(allVisibleIds));
  const clearSelection = () => setSelected(new Set());

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-ink">Pending tasks</h2>
          <p className="mt-1 text-sm text-muted">
            Auto-derived from your pipeline state. Click <strong>Open</strong> to jump into the keyword detail panel,
            or use the row actions to re-prioritise / re-cluster / archive (soft-delete) directly from here.
          </p>
        </div>
        <div className="rounded-xl bg-white p-3 ring-1 ring-line">
          <div className="text-2xs uppercase tracking-wide text-muted">Open tasks</div>
          <div className="text-2xl font-semibold text-ink">{isPending ? "—" : data?.total ?? 0}</div>
        </div>
      </div>

      {/* Resolve with AI — only when there is something to resolve. */}
      {(data?.byCategory.preflight ?? []).length > 0 && (
        <ResolvePanel
          onApplied={() => {
            void qc.invalidateQueries({ queryKey: ["dashboard"] });
            void qc.invalidateQueries({ queryKey: ["pipeline"] });
          }}
          onError={(text) => setBanner({ kind: "ok", text })}
        />
      )}

      {/* Category tabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        <CategoryChip
          active={activeCategory === "all"}
          onClick={() => setActiveCategory("all")}
          label="All"
          count={data?.total ?? 0}
        />
        {CATEGORY_ORDER.map((c) => {
          const tasks = data?.byCategory[c] ?? [];
          if (tasks.length === 0) return null;
          const meta = CATEGORY_META[c];
          return (
            <CategoryChip
              key={c}
              active={activeCategory === c}
              onClick={() => setActiveCategory(c)}
              label={meta.label}
              count={data?.totals?.[c] ?? tasks.length}
              icon={meta.icon}
            />
          );
        })}
      </div>

      {/* Banner */}
      {banner && (
        <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
          banner.kind === "ok" ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"
        }`}>
          {banner.kind === "ok" ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Bulk action bar — only visible when there's a selection */}
      {selectedIds.length > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          clusters={clustersQuery.data?.clusters ?? []}
          busy={bulkMutation.isPending || aiProgress !== null}
          aiProgress={aiProgress}
          onAiAssign={() => aiAssignSelected(selectedIds)}
          onArchive={() => bulkMutation.mutate({ action: "archive", ids: selectedIds })}
          onReject={(reason) => bulkMutation.mutate({ action: "reject", ids: selectedIds, reason })}
          onDelete={() => bulkMutation.mutate({ action: "delete", ids: selectedIds })}
          onSetPriority={(p) => bulkMutation.mutate({ action: "set_priority", ids: selectedIds, priority: p })}
          onSetCluster={(c) => bulkMutation.mutate({ action: "set_cluster", ids: selectedIds, clusterId: c })}
          onClear={clearSelection}
        />
      )}

      {/* Body */}
      {isPending ? (
        <TaskSkeletons />
      ) : activeCategory === "all" ? (
        groups.length === 0 ? (
          <SectionCard title="Inbox zero" icon={Sparkles}>
            <EmptyState
              icon={CheckCircle2}
              title="No pending tasks 🎉"
              description="The pipeline is in a clean state. Run a new keyword through Pipeline → Enrich to populate the inbox."
            />
          </SectionCard>
        ) : (
          <div className="space-y-5">
            <SelectAllToggle allSelected={allSelected} hasAny={allVisibleIds.length > 0} onSelectAll={selectAllVisible} onClear={clearSelection} />
            {groups.map((g) => (
              <CategorySection
                key={g.category}
                category={g.category}
                tasks={g.tasks}
                total={data?.totals?.[g.category]}
                clusters={clustersQuery.data?.clusters ?? []}
                selected={selected}
            highlightId={highlightId}
                onToggleSelect={toggleSelect}
                onChanged={invalidateAll}
                onError={(msg) => setBanner({ kind: "error", text: msg })}
              />
            ))}
          </div>
        )
      ) : (
        <>
          <SelectAllToggle allSelected={allSelected} hasAny={allVisibleIds.length > 0} onSelectAll={selectAllVisible} onClear={clearSelection} />
          <CategorySection
            category={activeCategory}
            tasks={visible}
            total={data?.totals?.[activeCategory]}
            clusters={clustersQuery.data?.clusters ?? []}
            selected={selected}
            highlightId={highlightId}
            onToggleSelect={toggleSelect}
            onChanged={invalidateAll}
            onError={(msg) => setBanner({ kind: "error", text: msg })}
            showHeader={false}
          />
        </>
      )}
    </div>
  );
}

function SelectAllToggle({
  allSelected, hasAny, onSelectAll, onClear,
}: {
  allSelected: boolean;
  hasAny: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  if (!hasAny) return null;
  return (
    <div className="flex items-center gap-3 text-xs text-muted">
      <button onClick={allSelected ? onClear : onSelectAll} className="font-medium text-brand-700 hover:underline">
        {allSelected ? "Clear selection" : "Select all visible"}
      </button>
    </div>
  );
}

function BulkActionBar({
  count, clusters, busy, aiProgress, onAiAssign, onArchive, onReject, onDelete, onSetPriority, onSetCluster, onClear,
}: {
  count: number;
  clusters: ClusterView[];
  busy: boolean;
  aiProgress: string | null;
  onAiAssign: () => void;
  onArchive: () => void;
  onReject: (reason: string) => void;
  onDelete: () => void;
  onSetPriority: (p: Priority) => void;
  onSetCluster: (clusterId: string | null) => void;
  onClear: () => void;
}) {
  const archive = () => {
    if (confirm(`Archive ${count} keyword${count === 1 ? "" : "s"}? They'll disappear from Tasks + Pipeline but stay in the DB.`)) {
      onArchive();
    }
  };
  const reject = () => {
    const reason = prompt(`Reject ${count} keyword${count === 1 ? "" : "s"}? (Optional reason)`, "");
    if (reason !== null) onReject(reason);
  };
  const hardDelete = () => {
    if (confirm(`PERMANENTLY DELETE ${count} keyword${count === 1 ? "" : "s"}? This cascades to extracts/distributions and is NOT reversible.`)) {
      onDelete();
    }
  };
  return (
    <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand-50 px-3 py-2 text-xs shadow-sm">
      <span className="font-medium text-brand-700">{count} selected</span>
      <span className="h-3 w-px bg-line" />
      <select
        onChange={(e) => { if (e.target.value) { onSetPriority(e.target.value as Priority); e.currentTarget.value = ""; } }}
        defaultValue=""
        disabled={busy}
        className="input h-7 max-w-[140px] text-xs"
      >
        <option value="">Set priority…</option>
        {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <ClusterPicker
        value={null}
        clusters={clusters}
        onChange={(id) => { if (id) onSetCluster(id); }}
        disabled={busy}
        compact={false}
        placeholder="Move to cluster…"
      />
      <button onClick={onAiAssign} disabled={busy} className="btn btn-secondary text-xs" title="Classify each selected keyword into its best-fit cluster">
        <Sparkles className="h-3 w-3" />
        {aiProgress ? `Assigning… ${aiProgress}` : "Assign clusters with AI"}
      </button>
      <button onClick={archive} disabled={busy} className="btn btn-secondary text-xs">
        <Archive className="h-3 w-3" />
        Archive
      </button>
      <button onClick={reject} disabled={busy} className="btn btn-ghost text-xs">
        Reject
      </button>
      <button onClick={hardDelete} disabled={busy} className="btn btn-ghost text-xs text-danger hover:bg-danger/10">
        <Trash2 className="h-3 w-3" />
        Delete
      </button>
      <button onClick={onClear} disabled={busy} className="ml-auto text-xs text-muted hover:text-ink">
        Clear
      </button>
    </div>
  );
}

function CategoryChip({
  active, onClick, label, count, icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon?: LucideIcon;
}) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "inline-flex items-center gap-2 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
          : "inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-muted-dark ring-1 ring-line hover:bg-canvas hover:text-ink"
      }
    >
      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />}
      <span>{label}</span>
      <span className={
        active
          ? "rounded-full bg-white/15 px-1.5 py-0.5 text-2xs font-semibold"
          : "rounded-full bg-line-soft px-1.5 py-0.5 text-2xs font-semibold"
      }>
        {count}
      </span>
    </button>
  );
}

function CategorySection({
  category, tasks, total, clusters, selected, highlightId, onToggleSelect, onChanged, onError, showHeader = true,
}: {
  category: PipelineTask["category"];
  tasks: PipelineTask[];
  total?: number;
  clusters: ClusterView[];
  selected: Set<string>;
  /** Keyword deep-linked via `#/tasks?open=…` — scrolled to and ringed. */
  highlightId: string | null;
  onToggleSelect: (id: string) => void;
  onChanged: () => void;
  onError: (msg: string) => void;
  showHeader?: boolean;
}) {
  const meta = CATEGORY_META[category];
  const trueCount = total ?? tasks.length;
  const capped = trueCount > tasks.length;
  return (
    <SectionCard
      title={showHeader ? `${meta.label} (${trueCount.toLocaleString()})` : meta.label}
      icon={meta.icon}
      padded={false}
    >
      {capped && (
        <div className="border-b border-line bg-amber-50/60 px-4 py-1.5 text-2xs text-amber-700">
          Showing the top {tasks.length} of {trueCount.toLocaleString()} — act on these or bulk-process the rest from Keyword Research.
        </div>
      )}
      <ul className="divide-y divide-line">
        {tasks.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            clusters={clusters}
            selected={selected.has(t.keywordId)}
            highlighted={t.keywordId === highlightId}
            onToggleSelect={() => onToggleSelect(t.keywordId)}
            onChanged={onChanged}
            onError={onError}
          />
        ))}
      </ul>
    </SectionCard>
  );
}

function TaskRow({
  task, clusters, selected, highlighted, onToggleSelect, onChanged, onError,
}: {
  task: PipelineTask;
  clusters: ClusterView[];
  selected: boolean;
  highlighted: boolean;
  onToggleSelect: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const meta = CATEGORY_META[task.category];
  const [busy, setBusy] = useState<"priority" | "cluster" | "archive" | "fold" | "enrich" | "dismiss" | "resolve" | "defer" | null>(null);
  const rowRef = useRef<HTMLLIElement>(null);
  const [showContext, setShowContext] = useState(false);
  const contextCount = (task.findings ?? []).filter((f) => f.resolution === "info").length;

  // Scroll the deep-linked row into view. `block: "center"` rather than the
  // default: the inbox has a sticky header, and "nearest" leaves the row tucked
  // underneath it looking like nothing happened.
  useEffect(() => {
    if (highlighted) rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);

  const onOpen = () => { window.location.hash = `/pipeline?open=${encodeURIComponent(task.keywordId)}`; };

  /**
   * Apply the model's fold: link these siblings as secondaries of this keyword.
   *
   * The server refuses rather than overwrites (a sibling already owned by
   * another primary, already a primary itself, or already carrying an article),
   * so a partial result is the normal case and has to be reported — silently
   * linking 4 of 6 would read as success.
   */
  const fold = async (siblingIds: string[]) => {
    setBusy("fold");
    try {
      const r = await api.foldSecondaries(task.keywordId, siblingIds);
      onError(
        r.skipped.length
          ? `Linked ${r.linked.length}. Skipped ${r.skipped.length}: ${r.skipped.map((s) => `${s.keyword} (${s.reason})`).join(", ")}`
          : `Linked ${r.linked.length} secondary keyword(s) to "${task.keyword}".`,
      );
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  /** Re-fetch the SERP — closes the enrichment-depth findings. */
  const reEnrich = async () => {
    setBusy("enrich");
    try {
      const r = await api.reEnrichKeyword(task.keywordId);
      onError(`Re-enriched "${task.keyword}" — ${r.paaQuestions} PAA question(s), ${r.competitors} competitor(s).`);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  /**
   * Dismiss a finding. For a `decide` finding this IS the resolution — there is
   * no write that closes "these two would be produced in the same run"; the
   * answer is that you looked and judged it. The dismissal is keyed by
   * (keyword, code) server-side so the next run does not re-raise it.
   */
  const dismiss = async (code: string, summary: string) => {
    setBusy("dismiss");
    try {
      await api.ackPreflightFinding(task.keywordId, code);
      onError(`Dismissed: ${summary.slice(0, 70)}${summary.length > 70 ? "…" : ""}`);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  /**
   * Resolve THIS keyword only.
   *
   * The batch proposal covers all 20 candidates, costs ~$0.22, takes ~50s and
   * holds production for every keyword it touches until the whole thing is
   * decided. When you only want to settle one row, that is the wrong unit of
   * work: this is ~$0.02, a few seconds, and it holds exactly one keyword.
   * It merges into the pending plan rather than replacing it.
   */
  const resolveOne = async () => {
    setBusy("resolve");
    try {
      const r = await api.proposeResolutions([task.keywordId]);
      const mine = (r.plan?.fixes ?? []).filter((f) => f.keywordId === task.keywordId);
      const actionable = mine.filter((f) => f.action !== "no_action");
      onError(
        actionable.length
          ? `Proposed for "${task.keyword}": ${actionable.map((f) => f.preview).join(" · ")} — review it in the panel at the top.`
          : `Reviewed "${task.keyword}" — no automatic fix applies, and the reasoning is now on the finding itself. ` +
            `If it says the findings are a false positive, Dismiss is the right call; if you would rather not decide now, use Not now.`,
      );
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  /**
   * "Not now" — pause this keyword without judging it.
   *
   * Dismissing a finding says "I looked, go ahead" and leaves the keyword
   * eligible. This says the other thing: I have not decided, so do not write
   * it. The autopilot needs no special handling — it walks the ranked list and
   * simply takes the next opportunity, which the response names so the swap is
   * visible rather than inferred.
   */
  const deferKeyword = async () => {
    setBusy("defer");
    try {
      const r = await api.deferKeyword(task.keywordId);
      onError(
        `"${task.keyword}" paused — the autopilot will skip it and write ${r.promoted[0] ? `“${r.promoted[0]}”` : "the next opportunity"} instead. Lift it any time from this row.`,
      );
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  const patch = async (body: Parameters<typeof api.patchKeyword>[1], tag: "priority" | "cluster") => {
    setBusy(tag);
    try {
      await api.patchKeyword(task.keywordId, body);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  const aiAssign = async () => {
    setBusy("cluster");
    try {
      const r = await api.aiAssignClusters([task.keywordId]);
      onError(summariseAiAssign(r.results));
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  const archive = async () => {
    if (!confirm(`Archive "${task.keyword}"? It'll disappear from Tasks + Pipeline but stay in the DB.`)) return;
    setBusy("archive");
    try {
      await api.patchKeyword(task.keywordId, { status: "archived" });
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  return (
    <li
      ref={rowRef}
      className={`flex items-start gap-3 px-5 py-3.5 transition hover:bg-canvas ${
        highlighted ? "bg-warn-soft/60 ring-2 ring-inset ring-warn" : selected ? "bg-brand-50/40" : ""
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-line text-brand focus:ring-brand"
        aria-label={`Select ${task.keyword}`}
      />
      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tint}`}>
        <meta.icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-ink" dir={task.language === "he" ? "rtl" : "ltr"}>
            {task.keyword}
          </span>
          {/* Inline priority editor */}
          <select
            value={task.priority}
            onChange={(e) => patch({ priority: e.target.value as Priority }, "priority")}
            disabled={busy !== null}
            className={`h-6 rounded-full border-0 px-2 text-2xs font-semibold uppercase tracking-wider focus:ring-1 ${
              task.priority === "urgent" ? "bg-danger/10 text-danger" :
              task.priority === "high"   ? "bg-warn/10 text-warn" :
              task.priority === "medium" ? "bg-brand/10 text-brand" :
                                            "bg-line text-muted"
            }`}
            title="Click to change priority"
          >
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {/* Inline cluster reassignment */}
          <ClusterPicker
            value={task.clusterId}
            clusters={clusters}
            onChange={(id) => patch({ clusterId: id }, "cluster")}
            onAiAssign={aiAssign}
            disabled={busy !== null}
            busy={busy === "cluster"}
          />
        </div>
        <div className="mt-0.5 text-xs text-muted" dir="auto">{task.title} — {task.description}</div>

        {/* Pre-flight findings, inline. One row per KEYWORD with its findings
            nested, rather than one row per finding — four rows for one keyword
            buried the one that mattered under three that did not. */}
        {task.findings?.length ? (
          <ul className="mt-1.5 space-y-1">
            {/* Context findings are collapsed. They repeat the same suggested
                angle on every SEO risk, so at full weight four of them bury the
                one line that needs a decision. */}
            {(showContext ? task.findings : task.findings.filter((f) => f.resolution !== "info")).map((f, i) => (
              <li
                key={i}
                className={`rounded border px-2 py-1 text-2xs ${
                  f.severity === "block"
                    ? "border-danger/30 bg-danger-soft text-red-800"
                    : "border-warn/30 bg-warn-soft text-amber-900"
                }`}
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* dir="auto" per text node: the deterministic checks are
                      English and the model's reasoning is Hebrew, and they sit
                      in the same list. Anything else left one of them reading
                      backwards. */}
                  <span className="font-semibold" dir="auto">{f.summary}</span>
                  <span className={`rounded px-1 py-px text-[10px] uppercase tracking-wide ${RESOLUTION_STYLE[f.resolution]}`}>
                    {RESOLUTION_LABEL[f.resolution]}
                  </span>
                  {f.source === "model" && <span className="opacity-60">AI review</span>}
                </div>
                {f.detail && <div className="mt-0.5 opacity-80" dir="auto">{f.detail}</div>}
                {f.fix && <div className="mt-0.5 font-medium" dir="auto">→ {f.fix}</div>}

                {/* The model looked at this and chose not to act. That verdict
                    is the most useful thing the resolver produces and it used
                    to land in a banner that scrolled away — leaving you staring
                    at a finding with no sign it had already been assessed. Here
                    it turns Dismiss from a guess into a defensible decision. */}
                {f.aiVerdict && (
                  <div className="mt-1.5 rounded border border-sky-300 bg-sky-50 px-2 py-1 text-sky-900" dir="auto">
                    <span className="font-semibold">AI reviewed — no automatic fix:</span> {f.aiVerdict}
                  </div>
                )}

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {/* Only the FOLD finding gets an apply button.
                      `model-split-siblings` says "leave these alone", which is
                      already the state — a button implying otherwise would
                      invite the exact cannibalisation this exists to prevent. */}
                  {f.resolution === "apply" && f.data?.siblings?.length ? (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => fold(f.data!.siblings!.map((x) => x.id))}
                      className="btn-secondary btn-sm"
                    >
                      {busy === "fold" ? "Linking…" : `Link ${f.data.siblings.length} as secondaries`}
                    </button>
                  ) : null}
                  {f.resolution === "enrich" && (
                    <button type="button" disabled={busy !== null} onClick={reEnrich} className="btn-secondary btn-sm">
                      {busy === "enrich" ? "Re-enriching…" : "Re-fetch SERP"}
                    </button>
                  )}
                  {/* Dismiss is on EVERY finding, because for the `decide` ones
                      it is the actual resolution — you looked, you judged, it is
                      handled. It sticks across future runs so the same call is
                      never asked of you twice. */}
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => dismiss(f.code, f.summary)}
                    className="btn-ghost btn-sm opacity-70 hover:opacity-100"
                    title="Dismiss — stays dismissed on future runs"
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {contextCount > 0 && (
          <button
            type="button"
            onClick={() => setShowContext((v) => !v)}
            className="mt-1 text-2xs text-muted underline hover:text-muted-dark"
          >
            {showContext ? "Hide" : "Show"} {contextCount} context note{contextCount === 1 ? "" : "s"} from the AI review
          </button>
        )}

        <code className="mt-1 inline-block rounded bg-line-soft px-1.5 py-0.5 font-mono text-2xs text-muted-dark">
          {task.hint}
        </code>
      </div>
      <div className="flex shrink-0 items-start gap-1">
        {task.category === "preflight" && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={resolveOne}
            title="Ask the model for a fix for THIS keyword only — cheaper and faster than the whole batch, and it holds only this one keyword"
            className="btn-secondary btn-sm"
          >
            {busy === "resolve" ? "Thinking…" : "Resolve"}
          </button>
        )}
        {task.category === "preflight" && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={deferKeyword}
            title="Not now — hold this keyword out of production without deciding. The autopilot takes the next opportunity instead."
            className="btn-ghost btn-sm text-muted"
          >
            {busy === "defer" ? "Pausing…" : "Not now"}
          </button>
        )}
        <button onClick={onOpen} className="btn-secondary btn-sm">
          Open
        </button>
        <button
          onClick={archive}
          disabled={busy !== null}
          className="btn-ghost btn-sm text-muted hover:text-danger"
          title="Archive (soft-delete) — disappears from Tasks + Pipeline"
        >
          <Archive className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

function TaskSkeletons() {
  return (
    <SectionCard title="Loading tasks…" icon={ClipboardList} padded={false}>
      <ul className="divide-y divide-line">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-start gap-4 px-5 py-3.5">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-2.5 w-2/3" />
            </div>
            <Skeleton className="h-8 w-16 rounded-md" />
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
